<template>
  <nav
    v-if="hasContent"
    :class="$style.navigator"
    aria-label="Table of contents"
  >
    <div :class="$style.title">
      <svg
        :class="$style.titleIcon"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
      </svg>
      <span :class="$style.titleText">On This Page</span>
    </div>

    <div ref="desktopCustomToc" :class="$style.desktopOnly">
      <template v-if="tocMode === 'custom'">
        <div
          :class="$style.tocHtml"
          @click.capture="handleCustomTocClick($event, false)"
          v-html="customHtml"
        />
      </template>
      <template v-else>
        <ul>
          <template v-for="section in tocTree" :key="section.id">
            <li
              :aria-current="activeId === section.id ? 'location' : undefined"
            >
              <a :href="`#${section.id}`" @click.prevent="scrollTo(section.id)">
                {{ section.text }}
              </a>

              <ul v-if="section.children.length">
                <li
                  v-for="child in section.children"
                  :key="child.id"
                  :aria-current="activeId === child.id ? 'location' : undefined"
                >
                  <a :href="`#${child.id}`" @click.prevent="scrollTo(child.id)">
                    {{ child.text }}
                  </a>
                </li>
              </ul>
            </li>
          </template>
        </ul>
      </template>
    </div>

    <button
      :class="[
        $style.mobileToggle,
        mobileDropdownOpen && $style.mobileToggleOpen,
      ]"
      type="button"
      aria-haspopup="true"
      :aria-label="
        mobileDropdownOpen
          ? 'Close article navigation'
          : 'Open article navigation'
      "
      :aria-expanded="mobileDropdownOpen"
      @click="mobileDropdownOpen = !mobileDropdownOpen"
    >
      <svg
        :class="$style.titleIcon"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
      </svg>
      <span :class="$style.titleText">On This Page</span>
      <svg
        :class="[
          $style.mobileToggleArrow,
          mobileDropdownOpen && $style.mobileToggleArrowOpen,
        ]"
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M9 5l7 7-7 7" />
      </svg>
    </button>

    <div
      v-if="mobileDropdownOpen"
      :class="$style.mobileDropdown"
      role="region"
      aria-label="Table of contents"
    >
      <div :class="$style.mobileDropdownPanel">
        <template v-if="tocMode === 'custom'">
          <div
            ref="mobileCustomToc"
            :class="$style.tocHtml"
            @click.capture="handleCustomTocClick($event, true)"
            v-html="customHtml"
          />
        </template>
        <template v-else>
          <ul>
            <template v-for="section in tocTree" :key="section.id">
              <li
                :aria-current="activeId === section.id ? 'location' : undefined"
              >
                <a
                  :href="`#${section.id}`"
                  @click.prevent="scrollTo(section.id, true)"
                >
                  {{ section.text }}
                </a>

                <ul v-if="section.children.length">
                  <li
                    v-for="child in section.children"
                    :key="child.id"
                    :aria-current="
                      activeId === child.id ? 'location' : undefined
                    "
                  >
                    <a
                      :href="`#${child.id}`"
                      @click.prevent="scrollTo(child.id, true)"
                    >
                      {{ child.text }}
                    </a>
                  </li>
                </ul>
              </li>
            </template>
          </ul>
        </template>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    targetSelector?: string
    tableOfContents?: TableOfContentsConfig | null
  }>(),
  {
    targetSelector: '[data-article-v]',
    tableOfContents: null,
  },
)

const emit = defineEmits<{
  (e: 'has-items', val: boolean): void
}>()

interface NavItem {
  id: string
  text: string
  level: 'h2' | 'h3'
}

const items = ref<NavItem[]>([])
const activeId = ref<string>('')
const mobileDropdownOpen = ref(false)
const desktopCustomToc = ref<HTMLElement | null>(null)
const mobileCustomToc = ref<HTMLElement | null>(null)
let headingObserver: IntersectionObserver | null = null
let domMutationObserver: MutationObserver | null = null
let findInterval: NodeJS.Timeout | null = null
const visibleElements = ref<Map<string, boolean>>(new Map())

interface TocNode {
  id: string
  text: string
  children: NavItem[]
}

interface TableOfContentsConfig {
  mode?: 'off' | 'auto' | 'custom'
  customHtml?: string
}

const tocMode = computed(() => props.tableOfContents?.mode ?? 'auto')
const customHtml = computed(
  () => props.tableOfContents?.customHtml?.trim() || '',
)
const hasContent = computed(() =>
  tocMode.value === 'custom' ? !!customHtml.value : items.value.length > 0,
)
const customTocItems = computed<NavItem[]>(() => {
  if (!import.meta.client || tocMode.value !== 'custom' || !customHtml.value) {
    return []
  }

  const doc = new DOMParser().parseFromString(customHtml.value, 'text/html')
  const anchors = Array.from(doc.querySelectorAll('a[href^="#"]'))
  const newItems: NavItem[] = []

  anchors.forEach((anchor, index) => {
    const href = anchor.getAttribute('href') || ''
    const id = decodeURIComponent(href.slice(1)).trim()
    if (!id) return

    const text = anchor.textContent?.trim() || id || `heading-${index}`

    newItems.push({
      id,
      text,
      level: 'h2',
    })
  })

  return newItems
})

const getArticleHeadings = () => {
  if (!import.meta.client) return []

  const container = document.querySelector(props.targetSelector)
  if (!container) return []

  return Array.from(container.querySelectorAll('h2, h3'))
}

const tocTree = computed<TocNode[]>(() => {
  const tree: TocNode[] = []
  let currentSection: TocNode | null = null

  items.value.forEach((item) => {
    if (item.level === 'h2') {
      currentSection = {
        id: item.id,
        text: item.text,
        children: [],
      }
      tree.push(currentSection)
      return
    }

    if (currentSection) {
      currentSection.children.push(item)
      return
    }

    tree.push({
      id: item.id,
      text: item.text,
      children: [],
    })
  })

  return tree
})

// Generate clean URL slugs from heading text
const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-') // Replace spaces with -
    .replace(/[^\w-]+/g, '') // Remove all non-word chars
    .replace(/--+/g, '-') // Replace multiple - with single -
    .replace(/^-+/, '') // Trim - from start of text
    .replace(/-+$/, '') // Trim - from end of text
}

// Extract headings from DOM
const parseHeadings = () => {
  if (!import.meta.client) return
  if (tocMode.value !== 'auto') return

  const headings = getArticleHeadings()
  if (headings.length === 0) {
    items.value = []
    return
  }

  const newItems: NavItem[] = []

  headings.forEach((el, index) => {
    const text = el.textContent?.trim() || ''
    if (!text) return

    // Ensure heading has an ID
    let id = el.id
    if (!id) {
      id = slugify(text) || `heading-${index}`
      // Check for duplicates in document
      let uniqueId = id
      let count = 1
      while (
        document.getElementById(uniqueId) &&
        document.getElementById(uniqueId) !== el
      ) {
        uniqueId = `${id}-${count}`
        count++
      }
      id = uniqueId
      el.id = id
    }

    newItems.push({
      id,
      text,
      level: el.tagName.toLowerCase() as 'h2' | 'h3',
    })
  })

  // Only update list if it changed to avoid unnecessary re-renders
  const hasChanged = JSON.stringify(newItems) !== JSON.stringify(items.value)
  if (hasChanged) {
    items.value = newItems
    nextTick(() => {
      setupIntersectionObserver()
    })
  }
  emit('has-items', newItems.length > 0)
}

const syncCustomHeadings = () => {
  if (!import.meta.client || tocMode.value !== 'custom') return

  const articleHeadings = getArticleHeadings()
  const newItems = customTocItems.value

  articleHeadings.forEach((heading, index) => {
    const item = newItems[index]
    if (!item) return

    if (heading.id !== item.id) {
      heading.id = item.id
    }
  })

  const hasChanged = JSON.stringify(newItems) !== JSON.stringify(items.value)

  if (hasChanged) {
    items.value = newItems
  }

  emit('has-items', newItems.length > 0)

  nextTick(() => {
    setupIntersectionObserver()
    applyCustomActiveState()
  })
}

const syncCurrentMode = () => {
  if (!import.meta.client) return

  if (tocMode.value === 'auto') {
    parseHeadings()
    return
  }

  if (tocMode.value === 'custom') {
    syncCustomHeadings()
  }
}

// Observe which headings are in view
const setupIntersectionObserver = () => {
  if (!import.meta.client) return
  if (items.value.length === 0) return

  if (headingObserver) {
    headingObserver.disconnect()
  }

  visibleElements.value.clear()

  const headingEls = items.value
    .map((item) => document.getElementById(item.id))
    .filter((el): el is HTMLElement => Boolean(el))
  if (headingEls.length === 0) return

  headingObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        visibleElements.value.set(entry.target.id, entry.isIntersecting)
      })

      // Determine the active heading
      // Find the first visible heading from top to bottom
      let foundActive = false
      for (const item of items.value) {
        if (visibleElements.value.get(item.id)) {
          activeId.value = item.id
          foundActive = true
          break
        }
      }

      // Fallback: If no heading is intersecting, find the one closest above the viewport
      if (!foundActive) {
        let bestHeadingId = ''
        let minDistance = -Infinity

        items.value.forEach((item) => {
          const el = document.getElementById(item.id)
          if (!el) return
          const rect = el.getBoundingClientRect()
          // Heading is above the current view threshold
          if (rect.top < 150) {
            if (rect.top > minDistance) {
              minDistance = rect.top
              bestHeadingId = item.id
            }
          }
        })

        if (bestHeadingId) {
          activeId.value = bestHeadingId
        } else if (items.value.length > 0 && !activeId.value) {
          // Default to first item if we haven't scrolled past anything
          activeId.value = items.value[0].id
        }
      }
    },
    {
      // rootMargin: Matches the viewport area below AppHeader (60px) and a comfort zone
      rootMargin: '-80px 0px -60% 0px',
      threshold: 0,
    },
  )

  headingEls.forEach((el) => headingObserver?.observe(el))
}

const scrollTo = (id: string, closeMobileDialog = false) => {
  const el = document.getElementById(id)
  if (!el) return

  const headerOffset =
    import.meta.client && window.innerWidth < 1024
      ? 150 // Extra breathing room for the sticky mobile TOC button
      : 90 // 60px header + 30px breathing room
  const elementPosition = el.getBoundingClientRect().top
  const offsetPosition = elementPosition + window.scrollY - headerOffset

  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  })

  // Update hash history without page jump
  history.pushState(null, '', `#${id}`)

  if (closeMobileDialog) {
    mobileDropdownOpen.value = false
  }
}

const handleCustomTocClick = (event: MouseEvent, closeOnSelect: boolean) => {
  const target = event.target as HTMLElement | null
  const link = target?.closest('a[href^="#"]') as HTMLAnchorElement | null
  const href = link?.getAttribute('href')

  if (!href) return

  const id = decodeURIComponent(href.slice(1))
  if (!id) return
  event.preventDefault()
  scrollTo(id, closeOnSelect)
}

const applyCustomActiveState = () => {
  if (!import.meta.client || tocMode.value !== 'custom') return

  const containers = [desktopCustomToc.value, mobileCustomToc.value].filter(
    (element): element is HTMLElement => Boolean(element),
  )

  containers.forEach((container) => {
    container
      .querySelectorAll('li[aria-current="location"]')
      .forEach((node) => {
        node.removeAttribute('aria-current')
      })

    if (!activeId.value) return

    const activeLink = container.querySelector(
      `a[href="#${CSS.escape(activeId.value)}"]`,
    ) as HTMLAnchorElement | null

    activeLink?.closest('li')?.setAttribute('aria-current', 'location')
  })
}

// Watch for target element creation and content updates dynamically
const initializeTracking = () => {
  if (!import.meta.client) return

  const container = document.querySelector(props.targetSelector)
  if (container) {
    syncCurrentMode()

    // Observe changes inside the article body (e.g. dynamic/lazy loads)
    if (domMutationObserver) {
      domMutationObserver.disconnect()
    }
    domMutationObserver = new MutationObserver(() => {
      syncCurrentMode()
    })
    domMutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
    })
  } else {
    // Retry finding the container if it's not rendered yet (e.g., Nuxt mounting lag)
    let attempts = 0
    if (findInterval) clearInterval(findInterval)
    findInterval = setInterval(() => {
      attempts++
      const found = document.querySelector(props.targetSelector)
      if (found) {
        if (findInterval) clearInterval(findInterval)
        initializeTracking()
      }
      if (attempts > 40) {
        if (findInterval) clearInterval(findInterval)
      }
    }, 150)
  }
}

onMounted(() => {
  nextTick(() => {
    initializeTracking()
  })
})

watch([activeId, mobileDropdownOpen, customHtml], () => {
  if (tocMode.value !== 'custom') return
  nextTick(() => {
    applyCustomActiveState()
  })
})

onBeforeUnmount(() => {
  if (headingObserver) headingObserver.disconnect()
  if (domMutationObserver) domMutationObserver.disconnect()
  if (findInterval) clearInterval(findInterval)
})
</script>

<style lang="scss" module>
.navigator {
  position: sticky;
  top: 80px; /* Comfortably below the 60px sticky AppHeader */
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 54rem;
  margin: 0 auto;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  overflow: visible;
  z-index: 1;

  @include media('lg') {
    display: block;
    background: var(--white);
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 12px 0 16px;
    margin: 0 auto 24px;
    width: auto;
    max-width: 54rem;
    max-height: calc(100vh - 140px);
    overflow-y: auto;
    overflow-x: clip;
    scrollbar-width: thin;

    /* Custom minimal scrollbar */
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: var(--gray-4, #e5e7eb);
    }
  }

  ul {
    list-style: none;
  }
}

.title {
  display: none;

  @include media('lg') {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #484848;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 12px;
    padding: 0 12px 8px;
  }
}

.titleText {
  font-family: var(--roboto-condensed);
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  line-height: 1;
}

.titleIcon {
  flex: 0 0 auto;
}

.desktopOnly {
  display: none;

  @include media('lg') {
    display: block;
  }
}

.mobileToggle {
  align-items: center;
  background: var(--white);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #484848;
  cursor: pointer;
  display: inline-flex;
  gap: 10px;
  justify-content: flex-start;
  align-self: flex-end;
  padding: 14px 18px;
  width: 100%;
}

.mobileToggleOpen {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom-color: transparent;
}

.mobileToggleArrow {
  flex: 0 0 auto;
  margin-left: auto;
  width: 16px;
  height: 16px;
  transition: transform 0.2s ease;
}

.mobileToggleArrowOpen {
  transform: rotate(90deg);
}

.mobileDropdown {
  left: 0;
  position: absolute;
  right: 0;
  top: calc(100% - 1px);
  z-index: 2;
  width: 100%;

  @include media('lg') {
    display: none;
  }
}

.mobileDropdownPanel {
  background: var(--white);
  border: 1px solid #e5e7eb;
  border-top: 0;
  border-radius: 0 0 8px 8px;
  padding: 10px 0 18px;
}

.desktopOnly > ul,
.mobileDropdownPanel > ul,
.tocHtml > ul {
  list-style: none;
  margin: 0;
  padding: 0 17px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: min(60vh, 520px);
  overflow-y: scroll;
  overflow-x: clip;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: #807dca #e5e7eb;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #e5e7eb;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb {
    background: #807dca;
    border-radius: 999px;
  }
}

.navigator li {
  position: relative;
  line-height: 1.3;
}

.navigator li > a {
  font-family: var(--roboto-condensed);
  display: inline-block;
  font-size: 15px;
  font-weight: 700;
  color: #484848;
  text-decoration: none;
  transition:
    color 0.2s ease,
    transform 0.2s ease;
}

.navigator li > a:hover {
  color: #807dca;
  text-decoration: none;
  transform: translateX(2px); /* Sleek premium micro-animation on hover */
}

.navigator li > ul {
  list-style: none;
  margin-top: 10px;
  padding-left: 0;
  margin-left: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.navigator li > ul > li > a {
  font-weight: 500;
  line-height: 1;
  margin-left: 12px;
}

.navigator li[aria-current='location'] > a {
  color: #807dca;
}

.navigator li[aria-current='location']::before {
  content: '';
  position: absolute;
  left: -17px;
  top: 3px;
  height: 1em;
  width: 3px;
  background: #807dca;
}

@include media('lg') {
  .mobileToggle {
    display: none;
  }
}
</style>
